import {
    IndexSymbol,
    INDEX_CONSTITUENT_SYMBOLS,
    INDEX_CONSTITUENT_WEIGHTS,
    INDEX_SPOT_TICKERS,
    MONEYCONTROL_INDEX_IDS,
    MONEYCONTROL_MARKETMAP_URL,
    DIRECT_MONEYCONTROL_MARKETMAP_URL,
    normalizeMoneycontrolSymbol,
    getIndexExchange,
    formatTvTicker,
    getAllIndexFormattedTickers,
    TRADINGVIEW_SCANNER_URL,
    DIRECT_TRADINGVIEW_SCANNER_URL,
    type StockQuote,
    type IndexConstituentsSummary
} from '../config/constants';

import { getOrWaitForAuthToken } from './firebase';
import { useMarketStore } from './store';

function parseVolume(volStr: any): number {
    if (!volStr) return 0;
    const s = String(volStr).toLowerCase().trim().replace(/,/g, '');
    if (s.endsWith('m')) return parseFloat(s) * 1000000;
    if (s.endsWith('k')) return parseFloat(s) * 1000;
    if (s.endsWith('cr')) return parseFloat(s) * 10000000;
    if (s.endsWith('l') || s.endsWith('lac') || s.endsWith('lakh')) return parseFloat(s) * 100000;
    const val = parseFloat(s);
    return isNaN(val) ? 0 : val;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
        'Accept': 'application/json, text/plain, */*',
    };
    try {
        const token = await getOrWaitForAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    } catch {}
    return headers;
}

/**
 * Primary Fetcher: Moneycontrol Live MarketMap API
 * Queries real-time constituent data for all indices in 1 single batched HTTP request (all=true).
 * Maximizes speed and preserves 97%+ of Cloudflare Free request quota.
 */
async function fetchMoneycontrolConstituents(): Promise<Map<string, StockQuote>> {
    const quotesMap = new Map<string, StockQuote>();
    const authHeaders = await getAuthHeaders();

    // 1. Try Single Batched Request (all=true)
    try {
        const batchRes = await fetch(`${MONEYCONTROL_MARKETMAP_URL}?all=true`, {
            headers: authHeaders,
            cache: 'no-store'
        });


        if (batchRes.ok) {
            const batchJson = await batchRes.json();
            const data = batchJson?.data || {};
            const spots = batchJson?.spots || {};

            // Store spot quotes for all 3 indices and sync to store
            const storeSpotLtp: Record<string, number> = {};
            const storeSpotMeta: Record<string, any> = {};

            for (const [spotSym, spotData] of Object.entries(spots)) {
                if (!spotData || typeof spotData !== 'object') continue;
                const s = spotData as { ltp: number; change: number; prevclose: number };
                const spotQuote: StockQuote = {
                    symbol: spotSym,
                    name: spotSym,
                    ltp: s.ltp || 0,
                    change: s.change || 0,
                    changePercent: s.prevclose > 0 ? ((s.change / s.prevclose) * 100) : 0,
                    volume: 0,
                    open: 0,
                    high: 0,
                    low: 0,
                };
                quotesMap.set(spotSym, spotQuote);
                quotesMap.set(`NSE:${spotSym}`, spotQuote);
                quotesMap.set(`BSE:${spotSym}`, spotQuote);

                if (s.ltp > 0) {
                    storeSpotLtp[spotSym] = s.ltp;
                    storeSpotMeta[spotSym] = {
                        dayChange: s.change || 0,
                        dayChangePercent: s.prevclose > 0 ? ((s.change / s.prevclose) * 100) : 0,
                    };
                }
            }

            // Sync spots into Zustand store so all index cards always have live LTP
            if (Object.keys(storeSpotLtp).length > 0) {
                try {
                    useMarketStore.setState((prev: any) => ({
                        indexLtp: { ...storeSpotLtp, ...prev.indexLtp },
                        marketMeta: { ...storeSpotMeta, ...prev.marketMeta },
                    }));
                } catch {}
            }

            for (const [indexSym, items] of Object.entries(data)) {
                if (!Array.isArray(items)) continue;
                for (const item of items) {
                    const sym = normalizeMoneycontrolSymbol(item);
                    const ltp = parseFloat(String(item.lastvalue || '0').replace(/,/g, ''));
                    const change = parseFloat(String(item.change || '0').replace(/,/g, ''));
                    const changePercent = parseFloat(String(item.percentchange || '0').replace(/,/g, ''));
                    const volume = parseVolume(item.volume);

                    const quote: StockQuote = {
                        symbol: sym,
                        name: item.shortname || sym,
                        ltp: isNaN(ltp) ? 0 : ltp,
                        change: isNaN(change) ? 0 : change,
                        changePercent: isNaN(changePercent) ? 0 : changePercent,
                        volume,
                        open: 0,
                        high: 0,
                        low: 0,
                    };

                    quotesMap.set(`${indexSym}:${sym}`, quote);
                    quotesMap.set(sym, quote);
                }
            }

            if (quotesMap.size >= 25) {
                return quotesMap;
            }
        }
    } catch {
        // Fall back to direct queries below
    }


    // 2. Fallback to individual index direct URLs if single batch failed
    const indexEntries = Object.entries(MONEYCONTROL_INDEX_IDS) as [IndexSymbol, number][];

    const fetchPromises = indexEntries.map(async ([indexSym, indId]) => {
        let response: Response | null = null;
        let spotResponse: Response | null = null;
        const primaryUrl = `${MONEYCONTROL_MARKETMAP_URL}?ind_id=${indId}`;
        const fallbackUrl = `${DIRECT_MONEYCONTROL_MARKETMAP_URL}&ind_id=${indId}`;
        const spotUrl = `https://appfeeds.moneycontrol.com/jsonapi/market/indices&format=json&ind_id=${indId}`;

        try {
            [response, spotResponse] = await Promise.all([
                fetch(primaryUrl, { headers: authHeaders, cache: 'no-store' }).catch(() => null),
                fetch(spotUrl, { headers: { 'Accept': 'application/json, text/plain, */*' }, cache: 'no-store' }).catch(() => null),
            ]);
        } catch {
            response = null;
        }

        if (!response || !response.ok) {
            try {
                response = await fetch(fallbackUrl, {
                    headers: { 'Accept': 'application/json, text/plain, */*' },
                    cache: 'no-store'
                });
            } catch {
                response = null;
            }
        }

        // Process Spot Response in Fallback
        if (spotResponse && spotResponse.ok) {
            try {
                const indJson = await spotResponse.json();
                const ind = indJson?.indices;
                if (ind) {
                    const ltp = parseFloat(String(ind.lastprice || '0').replace(/,/g, ''));
                    const chg = parseFloat(String(ind.change || '0').replace(/,/g, ''));
                    const prev = parseFloat(String(ind.prevclose || '0').replace(/,/g, ''));
                    if (!isNaN(ltp) && ltp > 0) {
                        const spotQuote: StockQuote = {
                            symbol: indexSym,
                            name: indexSym,
                            ltp,
                            change: isNaN(chg) ? 0 : chg,
                            changePercent: !isNaN(prev) && prev > 0 ? ((chg / prev) * 100) : 0,
                            volume: 0,
                            open: 0,
                            high: 0,
                            low: 0,
                        };
                        quotesMap.set(indexSym, spotQuote);
                        quotesMap.set(`NSE:${indexSym}`, spotQuote);
                        quotesMap.set(`BSE:${indexSym}`, spotQuote);

                        try {
                            useMarketStore.setState((prevStore: any) => ({
                                indexLtp: { [indexSym]: ltp, ...prevStore.indexLtp },
                                marketMeta: {
                                    ...prevStore.marketMeta,
                                    [indexSym]: {
                                        ...prevStore.marketMeta?.[indexSym],
                                        dayChange: isNaN(chg) ? 0 : chg,
                                        dayChangePercent: !isNaN(prev) && prev > 0 ? ((chg / prev) * 100) : 0,
                                    }
                                }
                            }));
                        } catch {}
                    }
                }
            } catch {}
        }

        if (!response || !response.ok) return;

        try {
            const json = await response.json();
            const items = json?.item || [];
            for (const item of items) {
                const sym = normalizeMoneycontrolSymbol(item);
                const ltp = parseFloat(String(item.lastvalue || '0').replace(/,/g, ''));
                const change = parseFloat(String(item.change || '0').replace(/,/g, ''));
                const changePercent = parseFloat(String(item.percentchange || '0').replace(/,/g, ''));
                const volume = parseVolume(item.volume);

                const quote: StockQuote = {
                    symbol: sym,
                    name: item.shortname || sym,
                    ltp: isNaN(ltp) ? 0 : ltp,
                    change: isNaN(change) ? 0 : change,
                    changePercent: isNaN(changePercent) ? 0 : changePercent,
                    volume,
                    open: 0,
                    high: 0,
                    low: 0,
                };

                quotesMap.set(`${indexSym}:${sym}`, quote);
                quotesMap.set(sym, quote);
            }
        } catch (err: any) {
            console.warn(`[LiveConstituents/MC] Failed to parse ${indexSym}:`, err?.message || err);
        }
    });

    await Promise.all(fetchPromises);
    return quotesMap;
}

/**
 * Fallback Fetcher: TradingView Scanner
 * Queries TradingView scanner with all tickers in 1 batch request.
 */
async function fetchTradingViewConstituents(): Promise<Map<string, StockQuote>> {
    const quotesMap = new Map<string, StockQuote>();
    const allTickers = getAllIndexFormattedTickers();
    const authHeaders = await getAuthHeaders();

    if (allTickers.length === 0) return quotesMap;

    const requestBody = JSON.stringify({
        symbols: { tickers: allTickers },
        columns: [
            'name',
            'close',       // LTP
            'change_abs',  // Day change in ₹
            'change',      // Day change %
            'volume',      // Volume
            'open',        // Day Open
            'high',        // Day High
            'low'          // Day Low
        ]
    });

    const requestHeaders = {
        'Content-Type': 'application/json',
        ...authHeaders,
    };

    let response: Response | null = null;

    // 1. Try Primary Proxy URL
    try {
        response = await fetch(TRADINGVIEW_SCANNER_URL, {
            method: 'POST',
            headers: requestHeaders,
            body: requestBody,
            cache: 'no-store'
        });
    } catch {
        response = null;
    }


    // 2. Try Direct Fallback URL
    if (!response || !response.ok) {
        try {
            response = await fetch(DIRECT_TRADINGVIEW_SCANNER_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
                body: requestBody,
                cache: 'no-store'
            });
        } catch (err: any) {
            console.warn('[LiveConstituents/TV] Both proxy and direct fetch failed:', err?.message || err);
            return quotesMap;
        }
    }

    if (!response || !response.ok) {
        console.warn(`[LiveConstituents/TV] HTTP error ${response?.status || 'No Response'}`);
        return quotesMap;
    }

    try {
        const json = await response.json();
        const rows = json?.data || [];

        for (const row of rows) {
            if (!row || !row.s || !Array.isArray(row.d)) continue;
            const fullTicker = row.s as string;
            const rawSymbol = fullTicker.replace(/^(NSE:|BSE:)/i, '');
            const d = row.d;

            let displaySymbol = rawSymbol;
            if (rawSymbol === 'TMCV' || rawSymbol === 'TMPV') displaySymbol = 'TMPV';
            if (rawSymbol === 'BAJAJ_AUTO') displaySymbol = 'BAJAJ-AUTO';
            if (rawSymbol === 'M_M') displaySymbol = 'M&M';

            const quote: StockQuote = {
                symbol: displaySymbol,
                name: (d[0] as string) || displaySymbol,
                ltp: typeof d[1] === 'number' ? d[1] : 0,
                change: typeof d[2] === 'number' ? d[2] : 0,
                changePercent: typeof d[3] === 'number' ? d[3] : 0,
                volume: typeof d[4] === 'number' ? d[4] : 0,
                open: typeof d[5] === 'number' ? d[5] : 0,
                high: typeof d[6] === 'number' ? d[6] : 0,
                low: typeof d[7] === 'number' ? d[7] : 0,
            };

            quotesMap.set(fullTicker, quote);
            quotesMap.set(rawSymbol, quote);
            quotesMap.set(displaySymbol, quote);
        }
    } catch (err: any) {
        console.warn('[LiveConstituents/TV] Batch parse failed:', err?.message || err);
    }

    return quotesMap;
}

/**
 * Dual-Engine Batch Fetcher
 * 1. Primary: Moneycontrol Live MarketMap API (exact exchange match for BSE/NSE)
 * 2. Fallback: TradingView India Scanner
 */
export async function fetchAllConstituentQuotes(): Promise<Map<string, StockQuote>> {
    // 1. Try Primary: Moneycontrol
    try {
        const mcQuotes = await fetchMoneycontrolConstituents();
        if (mcQuotes.size >= 25) {
            return mcQuotes;
        }
    } catch (err: any) {
        console.warn('[LiveConstituents] Primary Moneycontrol failed, falling back to TradingView:', err?.message || err);
    }

    // 2. Fallback: TradingView Scanner
    return fetchTradingViewConstituents();
}

/**
 * Computes aggregated statistics for a specific index given the raw stock quotes map.
 */
export function computeIndexSummary(
    index: IndexSymbol,
    quotesMap: Map<string, StockQuote>
): IndexConstituentsSummary {
    const exchange = getIndexExchange(index);
    const rawSymbols = INDEX_CONSTITUENT_SYMBOLS[index] || [];
    const stocks: StockQuote[] = [];

    // Extract Index Spot Quote (LTP, Day Change, and Previous Close)
    const spotTicker = INDEX_SPOT_TICKERS[index] || `${exchange}:${index}`;
    const spotQuote = quotesMap.get(spotTicker) || quotesMap.get(index) || quotesMap.get(`NSE:${index}`) || quotesMap.get(`BSE:${index}`);
    let indexLtp = spotQuote ? spotQuote.ltp : 0;
    let indexDayChange = spotQuote ? spotQuote.change : 0;
    let indexDayChangePercent = spotQuote ? spotQuote.changePercent : 0;
    let indexPrevClose = spotQuote && spotQuote.ltp > 0 ? (spotQuote.ltp - spotQuote.change) : 0;

    // Baseline fallback if spot quote is temporarily unavailable
    if (indexPrevClose <= 0) {
        if (index === IndexSymbol.NIFTY) indexPrevClose = 24090.85;
        else if (index === IndexSymbol.BANKNIFTY) indexPrevClose = 57509.95;
        else if (index === IndexSymbol.SENSEX) indexPrevClose = 76933.59;
    }


    let totalDayChange = 0;
    let sumPercent = 0;
    let advances = 0;
    let declines = 0;
    let unchanged = 0;

    for (const sym of rawSymbols) {
        const fullTicker = formatTvTicker(sym, exchange);
        const quote = quotesMap.get(`${index}:${sym}`) || quotesMap.get(sym) || quotesMap.get(fullTicker) || {
            symbol: sym,
            name: sym,
            ltp: 0,
            change: 0,
            changePercent: 0,
            volume: 0,
            open: 0,
            high: 0,
            low: 0,
        };

        stocks.push(quote);
        totalDayChange += quote.change;
        sumPercent += quote.changePercent;

        if (quote.change > 0.0001) {
            advances++;
        } else if (quote.change < -0.0001) {
            declines++;
        } else {
            unchanged++;
        }
    }


    const totalStocks = stocks.length;
    const avgDayChangePercent = totalStocks > 0 ? sumPercent / totalStocks : 0;

    // Method 2: Free-Float Weighted Point Contribution Calculation
    const weights = INDEX_CONSTITUENT_WEIGHTS[index] || {};
    const weightSum = Object.values(weights).reduce((a, b) => a + b, 0) || 100;
    let projectedWeightedPts = 0;

    for (const quote of stocks) {
        const rawWeight = weights[quote.symbol] || (100 / (totalStocks || 1));
        const normalizedWeight = (rawWeight / weightSum) * 100;
        const ptImpact = indexPrevClose > 0 ? (indexPrevClose * normalizedWeight * quote.changePercent) / 10000 : 0;
        projectedWeightedPts += ptImpact;
    }

    const projectedIndex2 = indexPrevClose > 0 ? (indexPrevClose + projectedWeightedPts) : 0;

    // Sort for top 5 gainers (highest positive changePercent)
    const sortedDesc = [...stocks].sort((a, b) => b.changePercent - a.changePercent);
    const top5Gainers = sortedDesc.slice(0, 5);

    // Sort for top 5 losers (lowest negative changePercent)
    const sortedAsc = [...stocks].sort((a, b) => a.changePercent - b.changePercent);
    const top5Losers = sortedAsc.slice(0, 5);

    return {
        index,
        displayName: index === IndexSymbol.BANKNIFTY ? 'BANK NIFTY' : index,
        exchange,
        totalStocks,
        totalDayChange,
        avgDayChangePercent,
        advances,
        declines,
        unchanged,
        indexLtp,
        indexPrevClose,
        indexDayChange,
        indexDayChangePercent,
        projectedWeightedPts,
        projectedIndex2,
        top5Gainers,
        top5Losers,
        stocks,
        lastUpdated: new Date(),
        isLoading: false,
    };
}


